import React, { useState, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Sheet } from 'components/ui/sheet'
import { Button } from 'components/ui/button'
import PortfolioEditPopup from 'components/popups/PortfolioEditPopup'
import { Trash2, Briefcase } from 'lucide-react'
import { deletePortfolio, initializePortfolios, updatePortfolio } from 'slices/portfolioSlice'

interface PortfolioActionSheetProps {
	portfolio: any
	open: boolean
	onClose: () => void
	isOwner: boolean
	showDelete?: boolean
}

const PortfolioActionSheet = ({ portfolio, open, onClose, isOwner, showDelete = true }: PortfolioActionSheetProps) => {
	const dispatch = useDispatch()

	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)

	// Reset confirmation state when sheet opens/closes or portfolio changes
	useEffect(() => {
		setShowDeleteConfirm(false)
	}, [portfolio, open])

	if (!portfolio) {
		return (
			<Sheet open={false} onClose={onClose} side="right" className="!w-[500px]">
				<div />
			</Sheet>
		)
	}

	const handleEditPortfolio = async (values: any) => {
		const updatedPortfolio = { ...values, updateNote: 'Edited Portfolio.' }
		// @ts-ignore
		await dispatch(updatePortfolio(updatedPortfolio))
		// @ts-ignore
		await dispatch(initializePortfolios())
		onClose()
	}

	const handleDelete = async () => {
		// @ts-ignore
		await dispatch(deletePortfolio({ portfolioId: portfolio._id }))
		// @ts-ignore
		await dispatch(initializePortfolios())
		onClose()
	}

	return (
		<Sheet open={open} onClose={onClose} side="right" className="!w-[500px]">
			{/* Header */}
			<div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
				<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] shrink-0">
					<Briefcase className="w-5 h-5" />
				</div>
				<div className="min-w-0 flex-1">
					<h2 className="text-lg font-semibold text-gray-900 truncate">{portfolio.assetName}</h2>
					<p className="text-sm text-gray-500">Edit portfolio details and settings</p>
				</div>
			</div>

			{/* Body — Edit Form */}
			<div className="flex-1 overflow-y-auto p-5">
				<PortfolioEditPopup editPortfolio={handleEditPortfolio} recordForEdit={portfolio} />
			</div>

			{/* Footer — Delete (owner only) */}
			{isOwner && showDelete && (
				<div className="px-5 py-4 border-t border-gray-200 bg-gray-50">
					{showDeleteConfirm ? (
						<div className="flex items-center gap-2">
							<span className="text-sm text-red-600">Delete this portfolio?</span>
							<Button variant="danger" onClick={handleDelete}>
								Yes, Delete
							</Button>
							<Button variant="ghost" onClick={() => setShowDeleteConfirm(false)}>
								Cancel
							</Button>
						</div>
					) : (
						<Button variant="ghost-red" onClick={() => setShowDeleteConfirm(true)}>
							<Trash2 className="w-4 h-4 mr-1" />
							Delete
						</Button>
					)}
				</div>
			)}
		</Sheet>
	)
}

export default PortfolioActionSheet
